/**
 * Home dashboard — B1.
 * 5 persona variants; card sequence is the HOME_CARD_ORDER contract (H1/D1) —
 * one multi-column flow so mobile stacks in the contract order (balance in the
 * first viewport) and desktop flows the same DOM order into balanced columns.
 * Atlas: B1.
 */
"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { AlertTriangle, Users, Sparkles, CloudLightning, ClipboardCheck, Zap } from "lucide-react";
import { getOutageSeeds, getDemoStorm } from "@petci/fixtures";
import { useActivePersona } from "@/lib/persona";
import { formatCurrency, formatDate } from "@/lib/format";
import { useT, useLocaleTag } from "@/lib/locale";
import { FIXTURE_REQUESTS, SR_STATUS_TOKENS, SR_TYPE_KEYS, useDemoSessionStore, useBillState, useActiveStorm, useNow, useHurricaneSeason, useHurricaneAnchor, USAGE_TREND, HOME_CONTEXT_PER_PERSONA, isHotelPersona, INSIGHTS_PER_PERSONA, P3_KITCHEN_ANOMALY_RECENCY, countActiveOutages } from "@petci/shared-ui";
import type { BillState, PaymentEntry } from "@petci/shared-ui";
import { BalanceCard } from "@/components/BalanceCard";
import { HomeContextCard } from "@/components/HomeContextCard";
import { PelicanInsightsCard } from "@/components/PelicanInsightsCard";
import { AlertStatusStrip } from "@/components/AlertStatusStrip";
import { UsageSparkline } from "@/components/UsageSparkline";
import { VacantModeBadge } from "@/components/VacantModeBadge";
import { TermTooltip } from "@/components/TermTooltip";
import { IdentityStrip } from "@/components/IdentityStrip";
import { PortfolioBand } from "@/components/PortfolioBand";
import { ResortBand } from "@/components/ResortBand";
import { visibleHomeCards, heroBandCards, type HomeCardId } from "@/lib/home-card-order";
import { webHomeAlertInputs } from "@/lib/home-alert-inputs";


export default function HomePage() {
  const persona = useActivePersona();
  const t = useT();
  const trendPct = USAGE_TREND[persona.id];
  const costDeltaLabel = t("home.usageCostTrend", { sign: trendPct > 0 ? "+" : "", pct: trendPct });
  const now = useNow();
  // One resolver for balance, due date and settled-ness — the same one mobile
  // reads, so the two surfaces cannot disagree about this persona today.
  const bill = useBillState(persona.id);
  const hurricaneNow = useHurricaneAnchor(process.env.NEXT_PUBLIC_HURRICANE_ANCHOR_NOW);
  const inSeason = useHurricaneSeason(process.env.NEXT_PUBLIC_FORCE_SEASON);
  const storm = useActiveStorm(hurricaneNow, getDemoStorm(hurricaneNow), inSeason);
  const hurricaneSeason = storm.active;
  const allOutageSeeds = getOutageSeeds(now);
  // Filter outages to the active persona's service feeder for the location-specific pill.
  // The network-wide count card intentionally uses the full set.
  const personaOutages = persona.serviceCircuitId
    ? allOutageSeeds.filter((o) => o.circuitId === persona.serviceCircuitId)
    : allOutageSeeds;
  // R-P3-4: this rule now lives in shared-ui so mobile derives the identical
  // number. Behaviour-preserving here — web already had the correct definition.
  const activeOutageCount = countActiveOutages(allOutageSeeds);

  const managedAccounts = persona.managedAccounts ?? [];
  const anomalyAccounts = managedAccounts.filter((a) => a.anomalyBadge);
  const flags = {
    hurricaneSeason,
    hasContext: HOME_CONTEXT_PER_PERSONA[persona.id] != null,
    hasInsights: INSIGHTS_PER_PERSONA[persona.id].length > 0,
    hasUsage: persona.bills.length > 0,
    isHotel: isHotelPersona(persona.id),
    hasHotelKpi: persona.hotelKpi != null,
    hasAnomaly: anomalyAccounts.length > 0,
    hasManagedAccounts: managedAccounts.length > 0,
  };
  const cards = visibleHomeCards(persona.id, flags);
  // CD-1: the ids the full-rail hero band owns at lg+. Empty for residential.
  // The band and the column entries are BOTH in the DOM at every width; the
  // breakpoint decides which one paints, so there is no viewport JS and no
  // hydration branch — and below lg the page is byte-identical to before.
  const bandCards = heroBandCards(persona.id, flags);

  // Alert-aware status strip (H3/D5): aggregate the persona's active conditions
  // for the greeting-row strip. Derivation is web-side; ranking + labels come
  // from the shared route-free resolver so mobile reuses it in Phase 3.
  // R2 dedupe (combined-CHECK §2-R2): the layout HurricaneBanner already carries
  // the storm, so the web strip EXCLUDES it (via webHomeAlertInputs) and
  // headlines the next-highest persona alert. The shared resolver is untouched.
  const personaActiveOutage = personaOutages.find(
    (o) => o.status === "ACTIVE_NO_ETA" || o.status === "ACTIVE_WITH_ETA",
  );
  const alertInputs = webHomeAlertInputs({
    outage: {
      active: personaActiveOutage != null,
      circuitName: personaActiveOutage?.circuitName,
      outageId: personaActiveOutage?.id,
    },
    demandNearThreshold: flags.isHotel,
    anomaly: {
      count: anomalyAccounts.length,
      accountNumber: anomalyAccounts[0]?.accountNumber,
    },
  });

  const hour = now.getHours();
  const greetingKey =
    hour < 12 ? "greeting.morning" : hour < 18 ? "greeting.afternoon" : "greeting.evening";
  const firstName = persona.contactName.split(" ")[0] ?? persona.contactName;

  const usageChart = (
    <div className="card-surface space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-neutral-700">{t("home.usage12mo")}</h2>
        <span
          className={`badge ${trendPct > 0 ? "bg-semantic-warning/10 text-semantic-warning-text" : "bg-semantic-success/10 text-semantic-success-text"}`}
        >
          {costDeltaLabel}
        </span>
      </div>
      <UsageSparkline bills={persona.bills} />
    </div>
  );

  // Card registry — the JSX for each contract id. Only ids returned by
  // visibleHomeCards() are rendered, so the null entries below are never mounted.
  const registry: Record<HomeCardId, ReactNode> = {
    balance:
      persona.id === "P4" ? (
        <PortfolioSummaryCard persona={persona} state={bill} />
      ) : (
        <BalanceCard
          amount={bill.remaining}
          cycle={bill.cycle}
          isSettled={bill.isSettled}
          settledOnIso={bill.settledOnIso}
          accountNumber={persona.accountNumber}
          hasBill={bill.hasBill}
        />
      ),
    // Assistant entry point #1 (D4) — the persona-agnostic full-page bill
    // deep-dive. D-O1 moved it out of page chrome and INTO the contract, after
    // the money: as chrome it was a 267px advertisement (measured at 375px in
    // es) sitting between the status strip and the balance, pushing P4's
    // balance and P1's pay row below the fold. The first ten seconds answer
    // "what do I owe and can I pay it"; the AI offer earns its place directly
    // after that answer, not in front of it. Absent for P5 by contract — no
    // bill, so no bill deep-dive to advertise.
    "ai-callout": (
      <Link
        href="/bill/explain"
        className="flex items-center gap-4 card-surface hover:shadow-lg transition-shadow duration-base group"
      >
        <span className="w-12 h-12 rounded-lg bg-brand-primary flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-fast">
          <Sparkles size={22} className="text-white" aria-hidden />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-neutral-900">{t("bill.confusedAboutBill")}</p>
          <p className="text-xs text-neutral-500 mt-0.5">{t("home.aiCalloutSub")}</p>
        </div>
        <span className="text-brand-primary-text text-sm font-medium flex-shrink-0 group-hover:translate-x-0.5 transition-transform duration-fast">
          {t("home.askNow")}
        </span>
      </Link>
    ),
    // Assistant entry point #2 (paired with the /bill/explain callout above):
    // each insight's "Tell me more" routes to /support's
    // conversational AssistantPanel, seeded with that insight's prompt +
    // intentId (routing lives in PelicanInsightsCard.handleAction). Complements
    // — does not duplicate — the full-page deep-dive.
    insights: <PelicanInsightsCard />,
    context: <HomeContextCard />,
    hurricane: (
      <div className="card-warning border-l-4 border-l-semantic-warning border border-semantic-warning/20 space-y-2">
        <div className="flex items-center gap-2">
          <CloudLightning size={14} className="text-semantic-warning-text flex-shrink-0" aria-hidden />
          <p className="text-xs font-semibold text-semantic-warning-text uppercase tracking-wider">
            {t("hurricane.seasonActive")}
          </p>
        </div>
        <p className="text-xs text-neutral-700">{t("hurricane.preparedness")}</p>
        <Link href="/hurricane" className="text-xs text-brand-primary-text hover:underline py-1 -my-1">
          {t("hurricane.viewChecklist")}
        </Link>
      </div>
    ),
    usage: usageChart,
    identity: <IdentityStrip />,
    "network-status": (
      <div className="card-surface space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-neutral-700">{t("home.networkStatus")}</h2>
          <Link href="/outage/map" className="text-xs text-brand-primary-text hover:underline py-1 -my-1">
            {t("home.viewMap")}
          </Link>
        </div>
        {activeOutageCount > 0 ? (
          <Link
            href="/outage/map"
            className="inline-flex items-center gap-2 rounded-full bg-semantic-error/10 text-semantic-error px-3 py-1.5 text-sm font-medium hover:bg-semantic-error/20 transition-colors duration-fast"
            aria-label={t(
              activeOutageCount === 1 ? "home.activeOutagesAriaOne" : "home.activeOutagesAriaOther",
              { count: activeOutageCount },
            )}
          >
            <span className="w-2 h-2 rounded-full bg-semantic-error flex-shrink-0" />
            {t(
              activeOutageCount === 1 ? "home.outageCountOne" : "home.outageCountOther",
              { count: activeOutageCount },
            )}
          </Link>
        ) : (
          <span className="inline-flex items-center gap-2 rounded-full bg-semantic-success/10 text-semantic-success-text px-3 py-1.5 text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-semantic-success flex-shrink-0" />
            {t("home.allPowerOn")}
          </span>
        )}
      </div>
    ),
    "outage-report": (
      <Link
        href="/outage/map?action=report"
        className="card-surface flex items-center gap-3 border-l-4 border-l-semantic-error hover:shadow-lg transition-shadow duration-base group"
      >
        <span className="w-10 h-10 rounded-lg bg-semantic-error/10 flex items-center justify-center flex-shrink-0">
          <Zap size={20} className="text-semantic-error" aria-hidden />
        </span>
        <div className="flex-1">
          <p className="text-sm font-semibold text-neutral-900">{t("home.reportPowerOut")}</p>
          <p className="text-xs text-neutral-500">
            {t("home.reportPowerOutSub", { address: persona.serviceAddress.line1 })}
          </p>
        </div>
        <span className="text-xs font-semibold text-semantic-error group-hover:translate-x-0.5 transition-transform duration-fast">
          {t("home.report")}
        </span>
      </Link>
    ),
    "active-requests": <ActiveRequestsCard personaId={persona.id} />,
    activity: <ActivityFeed persona={persona} payments={bill.payments} />,
    // P3 peak-demand gauge + alert combined into one card, placed in the alert slot (H1).
    "peak-demand": (
      <div className="card-warning-subtle border-semantic-warning/30 space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-semantic-warning-text flex-shrink-0" aria-hidden />
          <p className="text-sm font-semibold text-neutral-800">
            <TermTooltip term="Peak Demand">{t("home.demand")}</TermTooltip> {t("home.nearThreshold")}
          </p>
        </div>
        <p className="text-sm">
          <span className="font-bold text-neutral-900">{t("home.kwValue", { value: 412 })}</span>
          <span className="text-neutral-500 text-xs">{t("home.ofKwThreshold", { cap: 450 })}</span>
        </p>
        <div className="w-full bg-neutral-100 rounded-full h-1.5">
          <div
            className="bg-semantic-warning h-1.5 rounded-full"
            style={{ width: "91.6%" }}
            role="progressbar"
            aria-valuenow={412}
            aria-valuemin={0}
            aria-valuemax={450}
            aria-label={t("home.peakDemandAria", { load: 412, cap: 450 })}
          />
        </div>
        <p className="text-xs text-neutral-500">
          {t("home.demandShedHint", { pct: "8.4%", cap: 450 })}
        </p>
      </div>
    ),
    "hotel-overview": (
      <Link
        href="/hotel/portfolio"
        className="card-accent border-brand-secondary/20 hover:shadow-lg transition-shadow duration-base space-y-2 group block"
      >
        <p className="text-xs font-semibold text-brand-secondary uppercase tracking-wider">
          {t("home.portfolioDashboard")}
        </p>
        <p className="text-sm font-semibold text-neutral-900">
          {t("home.portfolioMetersSub", { count: 6 })}
        </p>
        <span className="text-xs text-brand-primary-text font-medium group-hover:translate-x-0.5 transition-transform duration-fast inline-block">
          {t("home.openPortfolio")}
        </span>
      </Link>
    ),
    "hotel-ecor": persona.hotelKpi ? (
      <div className="card-accent border-brand-secondary/20 space-y-2">
        <p className="text-xs font-semibold text-brand-secondary uppercase tracking-wider">
          <TermTooltip term="ECOR">ECOR</TermTooltip>
        </p>
        <p className="text-2xl font-display font-bold text-neutral-900">
          {formatCurrency(persona.hotelKpi.ecorUSD)}
        </p>
        <p className="text-xs text-neutral-500">{t("home.ecorPerRoomNight")}</p>
        <Link href="/hotel/ecor" className="text-xs text-brand-primary-text hover:underline py-1 -my-1">
          {t("home.liveOccupancySlider")}
        </Link>
      </div>
    ) : null,
    "anomaly-hero": <P4AnomalyHero accounts={anomalyAccounts} />,
    "portfolio-nav": managedAccounts[0] ? (
      <Link
        href={`/pm/account/${managedAccounts[0].accountNumber}`}
        className="card-dark hover:shadow-lg transition-shadow duration-base space-y-2 group block"
      >
        <p className="text-xs font-semibold text-white/50 uppercase tracking-wider">
          {t("home.managedProperties")}
        </p>
        <p className="text-sm font-semibold text-white">
          {t("home.managedPropertiesSub", { count: managedAccounts.length })}
        </p>
        <span className="text-xs text-brand-primary-light font-medium group-hover:translate-x-0.5 transition-transform duration-fast inline-block">
          {t("home.viewProperties")}
        </span>
      </Link>
    ) : null,
  };

  return (
    <div className="space-y-6">
      {/* Greeting + alert-aware status strip (H3/D5) */}
      <div className="flex items-center justify-between gap-3">
        <h1 className="min-w-0 truncate text-2xl font-display font-bold tracking-tight text-neutral-900">
          {t(greetingKey, { name: firstName })}
        </h1>
        <div className="flex-shrink-0">
          <AlertStatusStrip inputs={alertInputs} />
        </div>
      </div>

      {/* Commercial hero band (CD-1) — lg+ only, full rail, above the card
          columns. It is the ONLY thing between the greeting row and the
          contract now: D-O1 pulled the AI callout into the card list, so the
          band is chrome that leads with this persona's primary object rather
          than an advertisement in front of it. Residential personas render no
          band, so their page opens straight onto the contract. */}
      {persona.id === "P4" && bandCards.length > 0 && (
        <PortfolioBand accounts={managedAccounts} state={bill} />
      )}
      {persona.id === "P3" && persona.hotelKpi && bandCards.length > 0 && (
        <ResortBand kpi={persona.hotelKpi} projectedTotal={bill.total} />
      )}

      {/* Card contract (H1/D1): one multi-column flow. Mobile = single column in
          contract order (balance in the first viewport); desktop flows the SAME
          DOM order into balanced columns. `data-card-id` order === HOME_CARD_ORDER. */}
      <div className="columns-1 lg:columns-3 lg:[column-gap:1.5rem]">
        {cards.map((id) => (
          <div
            key={id}
            data-card-id={id}
            data-in-band={bandCards.includes(id) ? "true" : undefined}
            className={`mb-6 break-inside-avoid${bandCards.includes(id) ? " lg:hidden" : ""}`}
          >
            {registry[id]}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PortfolioSummaryCard({
  persona,
  state,
}: {
  persona: ReturnType<typeof useActivePersona>;
  state: BillState;
}) {
  const accounts = persona.managedAccounts ?? [];
  // The headline is what the portfolio still OWES, so a batch payment visibly
  // settles it. The per-property tiles below keep their gross bill amounts:
  // payments are recorded against the portfolio, not attributed to individual
  // accounts, so zeroing a tile would claim an attribution we do not have.
  const total = state.remaining;
  const t = useT();
  const tag = useLocaleTag();
  const dueIso = accounts[0]?.currentBillDueDate;

  return (
    <div className="card-dark rounded-2xl p-6 space-y-4">
      <div>
        <p className="text-white/60 text-xs font-medium uppercase tracking-wider">
          {t("home.portfolioBalance")}
        </p>
        <p className="text-4xl font-display font-bold mt-1 tabular-nums">
          {formatCurrency(total)}
        </p>
        <p className="text-white/70 text-sm mt-1">
          {state.isSettled
            ? t("home.accountsCountPaid", { count: accounts.length, date: formatDate(state.settledOnIso, tag) })
            : t("home.accountsCountDue", { count: accounts.length, date: formatDate(dueIso, tag) })}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {accounts.map((a) => (
          <Link
            key={a.accountNumber}
            href={`/pm/account/${a.accountNumber}`}
            className="bg-white/10 hover:bg-white/20 rounded-lg px-3 py-2 text-xs transition-colors duration-fast"
          >
            <p className="font-medium truncate">{a.propertyName}</p>
            <p className="text-white/70 font-mono tabular-nums">
              {formatCurrency(a.currentBillAmount)}
            </p>
            {a.anomalyBadge && (
              <span className="block mt-0.5">
                <VacantModeBadge label={t("home.anomaly")} />
              </span>
            )}
          </Link>
        ))}
      </div>
      <Link
        href="/batch-pay"
        className="inline-flex items-center gap-1.5 rounded-lg bg-white text-neutral-900 px-4 py-2 text-sm font-semibold hover:bg-neutral-100 transition-colors duration-fast"
      >
        {t("pay.batchPayAll")}
      </Link>
    </div>
  );
}

function ActivityFeed({
  persona,
  payments,
}: {
  persona: ReturnType<typeof useActivePersona>;
  /** This session's payments, newest first — prepended as real feed rows (D-3). */
  payments: PaymentEntry[];
}) {
  const t = useT();
  // Relative time labels (not frozen dates) so the feed never contradicts the
  // now-anchored billing narrative — see billing-clock.ts (P0-C).
  // P3 kitchen-anomaly recency is single-sourced with the Pelican Insights card
  // (H0.4); the TIME KEY is derived from that same shared const so keying (H2)
  // can't reintroduce the "last week" vs "Last month" drift.
  const RECENCY_TIME_KEY: Record<string, string> = {
    "last week": "home.time.lastWeek",
    "last month": "home.time.lastMonth",
    "this cycle": "home.time.thisCycle",
    "this month": "home.time.thisMonth",
  };
  const p3AnomalyTimeKey = RECENCY_TIME_KEY[P3_KITCHEN_ANOMALY_RECENCY] ?? "home.time.thisMonth"; // safe generic; "Recently" retired (H4.2)

  // Rows carry i18n keys, not English — the figures/proper-nouns are ICU params
  // (H2 / D6). The paid-window override rewrites the 💳 row's amount + time only;
  // its textKey already encodes the persona's payment verb (batch vs single).
  interface ActivityRow {
    icon: string;
    textKey: string;
    params?: Record<string, string | number>;
    timeKey: string;
  }
  const ACTIVITY_SEEDS: Record<string, ActivityRow[]> = {
    P1: [
      { icon: "💳", textKey: "home.activity.paymentReceived", params: { amount: "$298.40" }, timeKey: "home.time.lastMonth" },
      { icon: "📄", textKey: "home.activity.billIssued", params: { amount: "$311.84" }, timeKey: "home.time.thisMonth" },
      { icon: "⚡", textKey: "home.activity.outageResolved", params: { area: "Bight Park" }, timeKey: "home.time.lastMonth" },
      { icon: "🌞", textKey: "home.activity.solarExportCredited", params: { kwh: "142" }, timeKey: "home.time.lastMonth" },
      { icon: "📊", textKey: "home.activity.usageAbovePrior", params: { pct: "8.7%" }, timeKey: "home.time.thisCycle" },
    ],
    P2: [
      { icon: "💳", textKey: "home.activity.paymentReceived", params: { amount: "$640.00" }, timeKey: "home.time.lastMonth" },
      { icon: "📄", textKey: "home.activity.billIssued", params: { amount: "$665.40" }, timeKey: "home.time.thisMonth" },
      { icon: "⚡", textKey: "home.activity.demandChargeApplied", timeKey: "home.time.thisCycle" },
      { icon: "📊", textKey: "home.activity.usageAbovePrior", params: { pct: "6.2%" }, timeKey: "home.time.thisCycle" },
      { icon: "🔔", textKey: "home.activity.highUsageAlertSent", timeKey: "home.time.lastMonth" },
    ],
    P3: [
      { icon: "📄", textKey: "home.activity.billIssued", params: { amount: "$73,402.86" }, timeKey: "home.time.thisMonth" },
      { icon: "⚠️", textKey: "home.activity.kitchenMeterAnomaly", params: { pct: "+18%" }, timeKey: p3AnomalyTimeKey },
      { icon: "🌞", textKey: "home.activity.solarExport", params: { kwh: "4,218" }, timeKey: "home.time.lastMonth" },
      { icon: "⚡", textKey: "home.activity.demandNearMiss", params: { load: "412", cap: "450" }, timeKey: "home.time.today" },
      { icon: "💳", textKey: "home.activity.paymentReceived", params: { amount: "$71,100.00" }, timeKey: "home.time.lastMonth" },
    ],
    P4: [
      { icon: "📄", textKey: "home.activity.billsIssuedTotal", params: { count: 4, amount: "$2,550.63" }, timeKey: "home.time.thisMonth" },
      { icon: "⚠️", textKey: "home.activity.vacantAnomaly", params: { property: "Villa Sea Breeze" }, timeKey: "home.time.thisMonth" },
      { icon: "💳", textKey: "home.activity.batchPayment", params: { amount: "$2,488.74" }, timeKey: "home.time.lastMonth" },
      { icon: "🔔", textKey: "home.activity.highUsageAlertProperty", params: { property: "Sea Breeze" }, timeKey: "home.time.thisMonth" },
      { icon: "📊", textKey: "home.activity.portfolioTrend", params: { pct: "+2.3%" }, timeKey: "home.time.thisCycle" },
    ],
  };

  const seeds = ACTIVITY_SEEDS[persona.id] ?? [];
  // D-3: a payment the customer just made appears here, as itself. The retired
  // version rewrote the seeded 💳 row whenever the CALENDAR said "paid", which
  // announced a payment nobody had made; these rows exist only because there is
  // a ledger entry behind each one.
  const sessionRows: ActivityRow[] = payments.map((p) => ({
    icon: "💳",
    textKey: "home.activity.paymentReceived",
    params: { amount: formatCurrency(p.amount) },
    timeKey: "home.time.today",
  }));
  const items = [...sessionRows, ...seeds];

  // Empty state (e.g. CSR/admin with no bills): show an honest empty message
  // and suppress the "View all bills" link, which would otherwise dead-end
  // (H0.8).
  if (items.length === 0) {
    return (
      <div className="card-surface space-y-3">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-neutral-500" aria-hidden />
          <h2 className="text-sm font-semibold text-neutral-700">{t("home.recentActivity")}</h2>
        </div>
        <div className="flex flex-col items-center gap-2 py-6 text-neutral-600">
          <Users size={20} className="text-neutral-500" aria-hidden />
          <p className="text-xs">{t("home.noRecentActivity")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card-surface space-y-3">
      <div className="flex items-center gap-2">
        <Users size={14} className="text-neutral-500" aria-hidden />
        <h2 className="text-sm font-semibold text-neutral-700">{t("home.recentActivity")}</h2>
      </div>
      <ul className="space-y-2" role="list">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            <span aria-hidden="true" className="text-base leading-none mt-0.5">
              {item.icon}
            </span>
            <span className="flex-1 text-neutral-700">{t(item.textKey, item.params)}</span>
            <span className="text-neutral-500 whitespace-nowrap">{t(item.timeKey)}</span>
          </li>
        ))}
      </ul>
      <Link
        href="/bill"
        className="text-xs text-brand-primary-text hover:underline block text-right py-1 -my-1"
      >
        {t("home.viewActivity")}
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// P4 anomaly hero panel (Item N)
// ---------------------------------------------------------------------------
function P4AnomalyHero({
  accounts,
}: {
  accounts: { propertyName: string; accountNumber: string; anomalyBadge?: string | undefined }[];
}) {
  const t = useT();
  const first = accounts[0];
  if (!first) return null;

  return (
    <div className="card-warning border-l-4 border-l-semantic-warning space-y-3">
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="text-semantic-warning-text flex-shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-neutral-900">
            {t("home.propertiesFlagged", { count: accounts.length, total: 4 })}
          </p>
          <p className="text-xs text-neutral-500 mt-0.5">
            {first.propertyName} · {first.anomalyBadge}
          </p>
        </div>
      </div>
      <Link
        href={`/pm/anomaly/${first.accountNumber}`}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-semantic-warning-text hover:underline py-1 -my-1"
      >
        {t("home.viewInvestigation")}
      </Link>
    </div>
  );
}

function ActiveRequestsCard({ personaId }: { personaId: string }) {
  const t = useT();
  const submittedRequests = useDemoSessionStore((s) => s.getSubmittedRequests(personaId));
  const fixtureRequests = FIXTURE_REQUESTS[personaId] ?? [];
  const requests = [...submittedRequests, ...fixtureRequests].filter(
    (r) => r.status.state !== "completed" && r.status.state !== "phase-2",
  );

  if (requests.length === 0) {
    return (
      <div className="card-surface">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardCheck size={14} className="text-neutral-500" aria-hidden />
          <h2 className="text-sm font-semibold text-neutral-700">{t("home.activeRequests")}</h2>
        </div>
        <div className="flex flex-col items-center gap-2 py-6 text-neutral-600">
          <ClipboardCheck size={20} className="text-neutral-500" aria-hidden />
          <p className="text-xs">{t("home.noActiveRequests")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card-surface space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={14} className="text-neutral-500" aria-hidden />
          <h2 className="text-sm font-semibold text-neutral-700">{t("home.activeRequests")}</h2>
        </div>
        <Link href="/services/track" className="text-xs text-brand-primary-text hover:underline py-1 -my-1">
          {t("home.viewAll")}
        </Link>
      </div>
      {/* req.type is the shared ServiceRequestType enum, keyed journey-wide via
          SR_TYPE_KEYS (D3, W5); the status label is keyed via the SR-token labelKey.
          Home and Services/Track resolve the same keys so the SR vocabulary can
          never drift between the two surfaces. */}
      <ul className="space-y-2" role="list">
        {requests.slice(0, 3).map((req) => {
          const token = SR_STATUS_TOKENS[req.status.state];
          return (
            <li key={req.id} className="flex items-center gap-2 text-xs">
              <span className={`w-1.5 h-1.5 rounded-full ${token.bg.replace("/10", "")} flex-shrink-0`} />
              <span className="flex-1 text-neutral-700 truncate">{t(SR_TYPE_KEYS[req.type])}</span>
              <span className={`${token.text} font-medium`}>{t(token.labelKey)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
